import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Form, Formik } from 'formik';
import every from 'lodash/every';
import filter from 'lodash/filter';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import { useHistory } from 'react-router-dom';
import { BrandOverline } from '@yme/atoms/brand-overline';
import { Col, Row } from '@yme/atoms/grid';
import { useIsOnScreen, useWindowSize } from '@yme/hooks/index';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack300, colorCoreBlack900Base, colorCoreWhite1000 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel3, RegularTextLevel3 } from '@yme/tokens/typography';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { formatPrice } from '@yme/utils/formatPrice';
import { StickyNavBar, ButtonsNavBar, ReturnedItemsList } from '../components';
import { Reimbursement } from '../components/Reimbursement';
import { FormikFields, StepRoutes } from '../constants';
import { addDataToReturnedFormItems, getParcelByNumber, getReimbursement, isItemReasonFilled, } from '../helpers';
import { ContentContainerStyled, FlexWrapperStyled, InfoSectionContainerStyled } from './styled';
export const SelectItemsStep = memo(({ stepData: { returnReasons }, cachedData: { orderDetails: { orderEntries, shippingCost, deliveries }, parcelNumber, returnedFormItems, }, setCachedData, }) => {
    const history = useHistory();
    const { language, t } = useTranslation();
    const parcels = deliveries.reduce((acc, el) => {
        return [...acc, ...el.parcels];
    }, []);
    const { isMobileView } = useWindowSize();
    const formikRef = useRef(null);
    const parcelItems = useMemo(() => {
        const parcel = getParcelByNumber(parcels, parcelNumber);
        return reduce(orderEntries, (acc, curr) => {
            const parcelItem = find(parcel?.items, {
                id: curr.id,
            });
            if (parcelItem) {
                const item = {
                    ...curr,
                    quantity: parcelItem.quantity,
                };
                return [...acc, item];
            }
            return acc;
        }, []);
    }, [orderEntries, parcels, parcelNumber]);
    const formikInitialValues = useMemo(() => returnedFormItems ||
        map(parcelItems, ({ sku }) => ({
            sku,
            [FormikFields.RETURN_REASON_CODE]: null,
            [FormikFields.RETURN_REASON_TEXT]: '',
            [FormikFields.RETURN_REASON_OTHER]: '',
            [FormikFields.RETURN_QUANTITY]: 0,
        })), [parcelItems, returnedFormItems]);
    const handleReasonChange = useCallback((sku, reasonCode, reasonText) => {
        const { values, setValues } = formikRef.current;
        const nextValues = map(values, (item) => {
            if (item.sku === sku) {
                return {
                    ...item,
                    [FormikFields.RETURN_REASON_CODE]: reasonCode,
                    [FormikFields.RETURN_REASON_TEXT]: reasonText,
                    [FormikFields.RETURN_REASON_OTHER]: '',
                };
            }
            return item;
        });
        setValues(nextValues);
    }, []);
    const handleOtherReasonTextChange = useCallback((sku, otherReasonText) => {
        const { values, setValues } = formikRef.current;
        const nextValues = map(values, (item) => {
            if (item.sku === sku) {
                return {
                    ...item,
                    [FormikFields.RETURN_REASON_OTHER]: otherReasonText,
                };
            }
            return item;
        });
        setValues(nextValues);
    }, []);
    const handleQuantityChange = useCallback((sku, quantity) => {
        const { values, setValues } = formikRef.current;
        const nextValues = map(values, (item) => {
            if (item.sku === sku) {
                return {
                    ...item,
                    [FormikFields.RETURN_QUANTITY]: Number(quantity),
                };
            }
            return item;
        });
        setValues(nextValues);
    }, []);
    const validateFormik = useCallback((values) => {
        const errors = {};
        const itemsWithFilledQuantity = filter(values, FormikFields.RETURN_QUANTITY);
        const isValid = !isEmpty(itemsWithFilledQuantity) && every(itemsWithFilledQuantity, isItemReasonFilled);
        if (!isValid) {
            set(errors, 'isValid', false);
        }
        return errors;
    }, []);
    const handleSubmit = useCallback((values) => {
        setCachedData((prevCachedData) => ({
            ...prevCachedData,
            returnedFormItems: values,
        }));
        history.push({
            pathname: `/${StepRoutes.SHIPPING_STEP}`,
        });
    }, [history, setCachedData]);
    const { isOnScreen: isReimbursementOnScreen, ref } = useIsOnScreen(1, '0px');
    useEffect(() => {
        setDigitalData(DigitalDataMutation.RETURN_PAGE_ITEMS_VIEW, null);
    }, []);
    return (React.createElement(Formik, { innerRef: formikRef, initialValues: formikInitialValues, validateOnMount: true, validate: validateFormik, onSubmit: handleSubmit }, ({ values, isValid }) => {
        const returnedItems = addDataToReturnedFormItems(values, parcelItems);
        const { total } = getReimbursement(parcels, returnedItems, shippingCost);
        const reimbursement = formatPrice({
            locale: language,
            currencyName: shippingCost.currencyCode,
            price: total,
        });
        const showStickyNavBar = isMobileView && returnedItems.length > 1 && !isReimbursementOnScreen;
        return (React.createElement(Form, null,
            React.createElement(FlexWrapperStyled, { flexDirection: "column" },
                React.createElement(ContentContainerStyled, { width: "100%" },
                    React.createElement(Row, { justifyContent: "center" },
                        React.createElement(Col, { lg: 8, bgColor: colorCoreWhite1000 },
                            React.createElement(BrandOverline, { borderSize: "6px", borderColor: colorCoreBlack900Base },
                                React.createElement(InfoSectionContainerStyled, null,
                                    React.createElement(HeadingLevel3, null, t('returnForm_selectItems_header')),
                                    React.createElement(FlexWrapper, { mt: "8px" },
                                        React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, t('returnForm_selectItems_description'))),
                                    React.createElement(FlexWrapper, { mt: "32px", flexDirection: "column" },
                                        React.createElement(ReturnedItemsList, { items: returnedItems, isReadOnly: false, returnReasons: returnReasons, onReasonChange: handleReasonChange, onOtherReasonTextChange: handleOtherReasonTextChange, onQuantityChange: handleQuantityChange }),
                                        React.createElement("div", { ref: ref },
                                            React.createElement(Reimbursement, { items: returnedItems, shippingCost: shippingCost, parcels: parcels })))))))),
                React.createElement(ButtonsNavBar, { isNextDisabled: !isValid }),
                showStickyNavBar && (React.createElement(StickyNavBar, { isNextDisabled: !isValid, reimbursement: reimbursement })))));
    }));
});
SelectItemsStep.displayName = 'SelectItemsStep_ReturnForm';
